
import { useState, useEffect } from 'react';

interface PageLoaderProps {
  onLoadComplete: () => void;
  isPageReady: boolean;
}

const PageLoader = ({ onLoadComplete, isPageReady }: PageLoaderProps) => {
  const [progress, setProgress] = useState(0);
  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    // 显示logo动画
    const logoTimer = setTimeout(() => {
      setShowLogo(true);
    }, 300);

    // 进度条动画
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        // 如果页面还没准备好，进度条最多到90%
        const maxProgress = isPageReady ? 100 : 90;
        
        if (prev >= maxProgress) {
          if (isPageReady && prev >= 100) {
            clearInterval(progressTimer);
            // 延迟一点时间让用户看到完成状态
            setTimeout(() => {
              try {
                onLoadComplete();
              } catch (error) {
                console.warn('加载完成回调错误:', error);
              }
            }, 800);
            return 100;
          }
          return prev;
        }
        
        // 根据页面加载状态调整进度增长速度
        const increment = isPageReady ? Math.random() * 20 + 10 : Math.random() * 8 + 2;
        return Math.min(prev + increment, maxProgress);
      });
    }, 200);

    return () => {
      clearTimeout(logoTimer);
      clearInterval(progressTimer);
    };
  }, [onLoadComplete, isPageReady]);

  // 当页面准备好时，快速完成剩余进度
  useEffect(() => {
    if (isPageReady && progress >= 90) {
      const finalTimer = setTimeout(() => {
        setProgress(100);
      }, 300);
      
      return () => clearTimeout(finalTimer);
    }
  }, [isPageReady, progress]);

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-primary via-primary/95 to-secondary flex items-center justify-center">
      {/* 背景装饰 */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-accent/30 rounded-full animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-24 h-24 border border-accent/20 rounded-full animate-pulse delay-300"></div>
        <div className="absolute top-1/2 right-1/3 w-16 h-16 border border-accent/25 rounded-full animate-pulse delay-500"></div>
      </div>

      <div className="text-center">
        {/* Logo区域 */}
        <div className={`mb-8 transition-all duration-1000 ${showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative">
            {/* 主Logo */}
            <h1 className="text-5xl lg:text-6xl font-bold text-accent font-serif mb-2 tracking-wider">
              马帮瑞贡
            </h1>
            <p className="text-white/80 text-lg font-light tracking-widest">
              传承三百年马帮精神
            </p>
            
            {/* 装饰线条 */}
            <div className="flex items-center justify-center mt-4 space-x-4">
              <div className="w-12 h-px bg-accent"></div>
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
              <div className="w-12 h-px bg-accent"></div>
            </div>
          </div>
        </div>

        {/* 进度条 */}
        <div className={`w-64 mx-auto transition-all duration-1000 delay-500 ${showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="relative">
            <div className="w-full h-1 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-accent to-accent/80 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-white/60 text-sm mt-3 font-light">
              {Math.round(progress)}%
            </p>
          </div>
        </div>

        {/* 加载文字 */}
        <div className={`mt-6 transition-all duration-1000 delay-700 ${showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-white/70 text-sm font-light tracking-wide">
            {progress < 50 ? '正在加载页面资源...' : 
             progress < 90 ? '正在准备茶香体验...' : 
             '即将为您呈现地道普洱好茶...'}
          </p>
        </div>
      </div>

      {/* 茶叶飘落动画 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-bounce opacity-20"
            style={{
              left: `${15 + i * 12}%`,
              top: `${10 + (i % 4) * 20}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2.5 + (i % 3) * 0.5}s`
            }}
          >
            <i className="ri-leaf-line text-accent text-xl lg:text-2xl"></i>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageLoader;
